import JamSpinner from '@/src/components/common/JamSpinner/JamSpinner';
import { RoutePath } from '@/src/RoutePath';
import { useFlashbars } from '@/src/store/flashbar.context';
import { SimplifiedJamClient } from '@/src/api/sdk/SimplifiedJamClient';
import { useJamClient } from '@/src/hooks/useJamClient';
import { DashboardJamMiniEvent, JamEventTypes } from '@/src/types/JamDashboard';
import { getErrorMessage } from '@/src/utils/errors.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import {
  Box,
  Button,
  ContentLayout,
  Header,
  NonCancelableCustomEvent,
  Tabs,
  TabsProps,
} from '@amzn/awsui-components-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import JamList from '../../ui/molecules/MyJams/JamList/JamList';

const tabs = {
  [JamEventTypes.PastEvents]: RoutePath.MY_JAMS_PAST,
  [JamEventTypes.ActiveEvents]: RoutePath.MY_JAMS_ACTIVE,
  [JamEventTypes.UpcomingEvents]: RoutePath.MY_JAMS_UPCOMING,
};

interface MyJamsTabProps {
  onEventsLoaded: (count: number) => void;
}

interface MyJamsEventsTabProps extends MyJamsTabProps {
  eventType: JamEventTypes;
  errorHeader: string;
  fetchEvents: (jamClient: SimplifiedJamClient) => Promise<{ events?: unknown } | Error>;
}

const MyJamsEventsTab = ({ eventType, errorHeader, fetchEvents, onEventsLoaded }: MyJamsEventsTabProps) => {
  const [events, setEvents] = useState<DashboardJamMiniEvent[]>([]);
  const [isFetchingEvents, setIsFetchingEvents] = useState(true);
  const jamClient = useJamClient();
  const { addErrorFlashbar, clearFlashbars } = useFlashbars();

  useEffect(() => {
    // Ignore responses that resolve after the user has switched to another tab
    let isActive = true;

    const loadEvents = async () => {
      clearFlashbars();
      setIsFetchingEvents(true);

      const response = await fetchEvents(jamClient);

      if (!isActive) {
        return;
      }

      if (response instanceof Error) {
        setEvents([]);
        onEventsLoaded(0);
        setIsFetchingEvents(false);
        addErrorFlashbar(getErrorMessage(response), errorHeader);
        return;
      }

      const loadedEvents = (response.events ?? []) as DashboardJamMiniEvent[];

      setEvents(loadedEvents);
      onEventsLoaded(loadedEvents.length);
      setIsFetchingEvents(false);
    };

    void loadEvents();

    return () => {
      isActive = false;
    };
  }, []);

  if (isFetchingEvents) {
    return (
      <Box>
        <JamSpinner />
      </Box>
    );
  }

  return <JamList events={events} eventType={eventType} />;
};

const ActiveJamsTab = ({ onEventsLoaded }: MyJamsTabProps) => {
  const { t } = useTranslation();

  return (
    <MyJamsEventsTab
      onEventsLoaded={onEventsLoaded}
      eventType={JamEventTypes.ActiveEvents}
      errorHeader={t(i18nKeys.myJams.eventTabs.activeEvents)}
      fetchEvents={(jamClient) => jamClient.getParticipantOngoingEvents()}
    />
  );
};

const UpcomingJamsTab = ({ onEventsLoaded }: MyJamsTabProps) => {
  const { t } = useTranslation();

  return (
    <MyJamsEventsTab
      onEventsLoaded={onEventsLoaded}
      eventType={JamEventTypes.UpcomingEvents}
      errorHeader={t(i18nKeys.myJams.eventTabs.upcomingEvents)}
      fetchEvents={(jamClient) => jamClient.getParticipantUpcomingEvents()}
    />
  );
};

const PastJamsTab = ({ onEventsLoaded }: MyJamsTabProps) => {
  const { t } = useTranslation();

  return (
    <MyJamsEventsTab
      onEventsLoaded={onEventsLoaded}
      eventType={JamEventTypes.PastEvents}
      errorHeader={t(i18nKeys.myJams.eventTabs.pastEvents)}
      fetchEvents={(jamClient) => jamClient.getParticipantPastEvents()}
    />
  );
};

const MyJams = () => {
  const { t } = useTranslation();
  const [eventCount, setEventCount] = useState(0);
  const { pathname } = useLocation();
  const history = useHistory();
  const isMyJamsPath = pathname.includes(RoutePath.MY_JAMS);

  const currentTab = useMemo(() => {
    if (pathname.includes(RoutePath.MY_JAMS_PAST)) {
      return JamEventTypes.PastEvents;
    }

    if (pathname.includes(RoutePath.MY_JAMS_UPCOMING)) {
      return JamEventTypes.UpcomingEvents;
    }

    return JamEventTypes.ActiveEvents;
  }, [pathname]);

  if (isMyJamsPath) {
    return <Redirect to={RoutePath.MY_JAMS_ACTIVE} />;
  }

  const onTabChange = (tab: NonCancelableCustomEvent<TabsProps.ChangeDetail>) => {
    history.push(tabs[tab.detail.activeTabId as keyof typeof tabs]);
  };

  const onJamJoinAnEventLinkClick = () => {
    history.push(RoutePath.JOIN_AN_EVENT);
  };

  return (
    <ContentLayout
      disableOverlap
      header={
        <Box margin={{ top: 'l' }}>
          <Header
            description={t(i18nKeys.myJams.header.caption, {
              count: eventCount,
            })}
            actions={
              <Button variant="primary" onClick={onJamJoinAnEventLinkClick}>
                {t(i18nKeys.myJams.header.joinAnEvent)}
              </Button>
            }
          >
            {t(i18nKeys.myJams.header.title)}
            <Box margin={{ bottom: 'l' }} />
          </Header>
        </Box>
      }
    >
      <Box padding={'m'}>
        <Tabs
          i18nStrings={{
            scrollLeftAriaLabel: t(i18nKeys.myJams.eventTabs.gotoPrevTab),
            scrollRightAriaLabel: t(i18nKeys.myJams.eventTabs.gotoNextTab),
          }}
          tabs={[
            {
              label: t(i18nKeys.myJams.eventTabs.activeEvents),
              id: JamEventTypes.ActiveEvents,
              content: <ActiveJamsTab onEventsLoaded={setEventCount} />,
            },
            {
              label: t(i18nKeys.myJams.eventTabs.upcomingEvents),
              id: JamEventTypes.UpcomingEvents,
              content: <UpcomingJamsTab onEventsLoaded={setEventCount} />,
            },
            {
              label: t(i18nKeys.myJams.eventTabs.pastEvents),
              id: JamEventTypes.PastEvents,
              content: <PastJamsTab onEventsLoaded={setEventCount} />,
            },
          ]}
          activeTabId={currentTab}
          onChange={onTabChange}
        />
      </Box>
    </ContentLayout>
  );
};

export default MyJams;
